import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useCatalogStore } from '@/core/store/catalogs'
import type {
  BaseServiceOptions,
  Pagination,
  TImportFile,
  TRecordAction,
  TRequestCatalog,
} from '@/core/types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import {
  type TAdvisor,
  type TAdvisors,
  TCatalogs,
  TProjectCatalogs,
  categoriesCatalog,
  moduleExportHandler,
  moduleServiceHandlers,
  projectsCatalogHandler,
  serviceConfig,
} from '../../..'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, filters } = options
  const store = useCatalogStore()
  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TAdvisor>({
    model,
    currentPage: currentPage ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TAdvisor>(
    (payload) => moduleServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, advisor) => serviceHandlers.deleteRecord(advisor),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => moduleServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: (_, ids) => serviceHandlers.deleteRecords(ids),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TAdvisor, AxiosError, TAdvisor>(
    (payload: TAdvisor) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TAdvisor, AxiosError, TAdvisor>(
    (payload: TAdvisor) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TAdvisor, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const importRecords = useMutation<void, AxiosError, TImportFile>(
    (payload: TImportFile) => {
      const formData = new FormData()
      formData.append('importFile', payload.file)
      return moduleServiceHandlers.importRecords(formData)
    },
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const requestCatalogs = useMutation<TCatalogs, AxiosError, TRequestCatalog>(
    (payload: TRequestCatalog) => categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        store.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TAdvisors, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onLoadRecords?.(data.meta ?? defaultPagination)
        }
      },
      select: (payload) => ({
        ...payload,
        data: payload.data?.map((advisor) => ({
          ...advisor,
          project_ids: [...(advisor.project_ids ?? [])]?.map((project) =>
            typeof project === 'number' ? project : project.id,
          ),
        })),
      }),
    },
  )

  const projectsCatalog = useQuery<TProjectCatalogs, AxiosError>(
    [model],
    () => projectsCatalogHandler.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const exportRecords = async () =>
    await moduleExportHandler.exportRecords({
      dynamicFilters: filters?.loadRecords,
    })

  return {
    loadRecords,
    addRecord,
    editRecord,
    deleteRecords,
    deleteRecord,
    importRecords,
    actionRecord,
    requestCatalogs,
    projectsCatalog,
    exportRecords,
  }
}
